import React, { useEffect, useState } from "react";
import ResultText from "./ResultText";
import { popupTriggers } from "../data/popupConditions";
import { useTranslation } from "react-i18next";

export default function QuizNode({ node, answers, onSelect, selectedAnswer, lang }) {
    const [buttonsDisabled, setButtonsDisabled] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        if (!node) return;

        const hasPopup = Object.prototype.hasOwnProperty.call(popupTriggers, node.id);
        if (hasPopup) {
            setButtonsDisabled(true);
            const timer = setTimeout(() => setButtonsDisabled(false), 3000);
            return () => clearTimeout(timer);
        }

        setButtonsDisabled(false);
    }, [node]);

    if (!node) return null;

    const isResult = node.type === "result";

    // DB language fallback: if Arabic exists use it, otherwise Hebrew
    const displayContent =
        lang === "ar" && node?.content_ar ? node.content_ar : node.content;

    return (
        <div dir="rtl" className="text-center text-darktext space-y-4">
            {/* ✅ Typography wrapper (returns original sizing/weight like before) */}

            <div className="text-sm sm:text-base md:text-lg font-semibold text-darktext whitespace-pre-line leading-relaxed px-4 py-10 sm:py-6">
                <ResultText text={displayContent} lang={lang} />
            </div>

            {/* Result-specific text (i18n) */}
            {isResult && (
                <>
                    <div className="text-sm sm:text-base text-right px-4 max-w-3xl mx-auto space-y-2">
                        <p className="font-medium text-darktext leading-tight">
                            {t("result.extraIntro")}
                            <br />
                            {t("result.extraReturn")}
                            <br />
                            {t("result.moreInfo")}{" "}
                            <a
                                href="https://forms.monday.com/forms/b170271affa511e6276954d303880943?r=use1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-lime-700 hover:text-lime-800"
                            >
                                {t("result.moreInfoLink")}
                            </a>{" "}
                            {t("result.moreInfoTail")}
                        </p>

                        <p className="font-medium leading-relaxed">
                            <a
                                href="https://www.wtb.org.il/contact/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline text-lime-700 hover:text-lime-800"
                            >
                                {t("result.contactWtb")}
                            </a>
                        </p>

                        {lang === "ar" && (
                            <p className="font-medium leading-relaxed">
                                <a
                                    href="mailto:sadnaot.wtb@gmail.com"
                                    className="underline text-lime-700 hover:text-lime-800"
                                >
                                    sadnaot.wtb@gmail.com
                                </a>
                            </p>
                        )}
                    </div>

                    <div className="text-xs text-gray-500 mt-4 text-center px-4 sm:px-0">
                        {t("result.disclaimer")}
                    </div>
                </>
            )}

            {/* Answer buttons */}
            {!isResult && (
                <div className="flex flex-col items-center space-y-4 sm:mb-0">
                    {answers.map((answer) => {
                        const isSelected = selectedAnswer?.id === answer.id;

                        // DB language fallback for answers
                        const label =
                            lang === "ar" && answer?.label_ar ? answer.label_ar : answer.label;

                        return (
                            <button
                                key={answer.id}
                                onClick={() => onSelect(answer)}
                                aria-label={t("quiz.ariaChooseAnswer", { label })}
                                disabled={buttonsDisabled}
                                className={`cursor-pointer w-[240px] sm:w-[260px] py-2 px-4 border text-sm sm:text-base text-center font-medium transition shadow-md
                  ${isSelected ? "text-accent" : "text-darktext"}
                  bg-white`}
                            >
                                {label}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
}